import React, { useState, useEffect } from "react";
import {
  Box,
  Alert,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import SubscriptionSetup from "./SubscriptionSetup";
import BillingDashboard from "./BillingDashboard";
import { getSubscriptionStatus } from "../services/paymentService";

export default function BillingTab({ organizationId }) {
  const [loading, setLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [error, setError] = useState("");

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    loadSubscriptionStatus();
  }, [organizationId]);

  const loadSubscriptionStatus = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getSubscriptionStatus(organizationId);
      setHasActiveSubscription(data.hasActiveSubscription);
    } catch (error) {
      console.error("Error loading subscription status:", error);
      // If subscription endpoint doesn't exist or fails, assume no subscription
      setHasActiveSubscription(false);
      if (error.response?.status !== 404) {
        setError("Failed to load subscription status");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionCreated = (subscription) => {
    setHasActiveSubscription(true);
    enqueueSnackbar("Subscription created successfully!", { variant: "success" });
  };

  const handleSubscriptionChanged = () => {
    loadSubscriptionStatus();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!organizationId) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        You need to be part of an organization to manage billing.
      </Alert>
    );
  }

  return (
    <Box>
      {!hasActiveSubscription ? (
        <Box>
          <Typography variant="h6" gutterBottom>
            Billing & Subscription
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Set up your subscription to start using all features. You'll be charged $5 per room per month.
          </Typography>
          <SubscriptionSetup
            organizationId={organizationId}
            onSuccess={handleSubscriptionCreated}
          />
        </Box>
      ) : (
        <Box>
          <Typography variant="h6" gutterBottom>
            Billing & Subscription
          </Typography>
          <BillingDashboard
            organizationId={organizationId}
            onSubscriptionChange={handleSubscriptionChanged}
          />
        </Box>
      )}
    </Box>
  );
} 